import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Alert, Button, Form, Row, Col, Card, Table, Spinner, ButtonGroup } from 'react-bootstrap';
import { FaTrashAlt, FaArrowLeft, FaChartBar, FaPlus, FaMinus, FaEdit } from 'react-icons/fa';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend } from 'chart.js';
import EmployeeSidebar from '../../components/Sidebar';

ChartJS.register(BarElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend);

const COLORS = [
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 99, 132, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(255, 206, 86, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(255, 159, 64, 0.7)'
];

const EmployeeBookkeeping = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  const [bookkeepingData, setBookkeepingData] = useState({
    title: 'Financial Data',
    additionalTitle: 'Additional Data',
    headers: ['Row Name', 'Column1', 'Column2', 'Column3', 'Column4'],
    rows: [
      { name: 'Row1', Column1: '', Column2: '', Column3: '', Column4: '', include: true },
      { name: 'Row2', Column1: '', Column2: '', Column3: '', Column4: '', include: true },
      { name: 'Row3', Column1: '', Column2: '', Column3: '', Column4: '', include: true }
    ],
    chartData: null
  });

  const filteredRows = bookkeepingData?.rows?.filter(row => row.include) || [];
  const excludedRows = bookkeepingData?.rows?.filter(row => !row.include) || [];
  const dataColumns = bookkeepingData?.headers?.filter(h => h !== 'Row Name') || [];

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("employeeToken");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        // Fetch employee data
        const empResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!empResponse.ok) throw new Error("Failed to fetch employee data");
        const empData = await empResponse.json();
        setEmployee(empData.employee);

        // Check if client belongs to this employee
        const client = empData.employee.clients.find(c => c._id === userId);
        if (!client) throw new Error("Client not found or not assigned to you");

        // Fetch bookkeeping data
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          if (response.status === 404) {
            setLoading(false);
            return;
          }
          throw new Error("Failed to fetch bookkeeping data");
        }

        const data = await response.json();
        setBookkeepingData(prev => ({
          ...prev,
          ...data,
          headers: Array.isArray(data.headers) ? data.headers : prev.headers,
          rows: Array.isArray(data.rows) ? data.rows : prev.rows
        }));
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, navigate]);

  const generateChartData = () => {
    return {
      labels: filteredRows.map(row => row.name),
      datasets: dataColumns.map((header, index) => ({
        label: header,
        data: filteredRows.map(row => parseFloat(row[header]) || 0),
        backgroundColor: COLORS[index % COLORS.length],
        borderColor: COLORS[index % COLORS.length].replace('0.7', '1'),
        borderWidth: 1
      }))
    };
  };

  const handleSave = async () => {
    const token = localStorage.getItem("employeeToken");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const chartData = generateChartData();
      const dataToSave = {
        ...bookkeepingData,
        chartData
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dataToSave),
      });

      if (!response.ok) {
        throw new Error("Failed to save bookkeeping data");
      }

      const data = await response.json();
      setBookkeepingData(prev => ({
        ...prev,
        ...data,
        headers: Array.isArray(data.headers) ? data.headers : prev.headers,
        rows: Array.isArray(data.rows) ? data.rows : prev.rows,
        chartData: data.chartData || chartData
      }));
      setEditMode(false);
      setError("");
    } catch (error) {
      setError(error.message);
    }
  };

  const handleCellChange = (rowIndex, colName, value) => {
    const updatedRows = [...bookkeepingData.rows];
    updatedRows[rowIndex][colName] = value;
    setBookkeepingData(prev => ({
      ...prev,
      rows: updatedRows
    }));
  };

  const handleHeaderChange = (index, value) => {
    const newHeaders = [...bookkeepingData.headers];
    newHeaders[index] = value;
    setBookkeepingData(prev => ({
      ...prev,
      headers: newHeaders
    }));
  };

  const handleIncludeChange = (rowIndex, checked) => {
    const updatedRows = [...bookkeepingData.rows];
    updatedRows[rowIndex].include = checked;
    setBookkeepingData(prev => ({
      ...prev,
      rows: updatedRows
    }));
  };

  const addRow = () => {
    const newRowName = `Row${bookkeepingData.rows.length + 1}`;
    const newRow = { name: newRowName, include: true };
    bookkeepingData.headers.forEach(header => {
      newRow[header] = '';
    });
    setBookkeepingData(prev => ({
      ...prev,
      rows: [...prev.rows, newRow]
    }));
  };

  const removeRow = (rowIndex) => {
    const updatedRows = bookkeepingData.rows.filter((_, index) => index !== rowIndex);
    setBookkeepingData(prev => ({
      ...prev,
      rows: updatedRows
    }));
  };

  const addColumn = () => {
    const newHeader = `Column${bookkeepingData.headers.length}`;
    const updatedHeaders = [...bookkeepingData.headers, newHeader];
    const updatedRows = bookkeepingData.rows.map(row => ({
      ...row,
      [newHeader]: ''
    }));
    setBookkeepingData(prev => ({
      ...prev,
      headers: updatedHeaders,
      rows: updatedRows
    }));
  };

  const removeColumn = (colIndex) => {
    if (colIndex === 0) return;

    const headerToRemove = bookkeepingData.headers[colIndex];
    const updatedHeaders = bookkeepingData.headers.filter((_, index) => index !== colIndex);
    const updatedRows = bookkeepingData.rows.map(row => {
      const newRow = { ...row };
      delete newRow[headerToRemove];
      return newRow;
    });

    setBookkeepingData(prev => ({
      ...prev,
      headers: updatedHeaders,
      rows: updatedRows
    }));
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: bookkeepingData.title,
        font: { size: 18 }
      },
      animation: {
        duration: 2000,
        easing: 'easeInOutQuad'
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        ticks: {
          callback: function(value) {
            return '$' + value.toLocaleString();
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      <EmployeeSidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        employee={employee}
      />

      <div className="main-content-container">
        <Button variant="outline-primary" onClick={() => navigate(-1)} className="mb-4">
          <FaArrowLeft className="me-2" /> Back to Clients
        </Button>

        <Row className="align-items-center mb-4">
          <Col>
            <h2>Bookkeeping Management</h2>
            <p className="text-muted">Client ID: {userId}</p>
          </Col>
          <Col className="text-end">
            {editMode ? (
              <ButtonGroup>
                <Button variant="success" onClick={handleSave}>Save Changes</Button>
                <Button variant="secondary" onClick={() => setEditMode(false)}>Cancel</Button>
              </ButtonGroup>
            ) : (
              <Button variant="primary" onClick={() => setEditMode(true)}>
                <FaEdit className="me-2" /> Edit Data
              </Button>
            )}
          </Col>
        </Row>

        {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-white border-bottom-0">
            <ButtonGroup>
              <Button variant="outline-primary" active>Data Table</Button>
              <Button variant="outline-primary">
                <FaChartBar /> Graph Chart
              </Button>
            </ButtonGroup>
          </Card.Header>
          <Card.Body>
            {editMode && (
              <div className="mb-4">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Main Title</Form.Label>
                      <Form.Control
                        type="text"
                        value={bookkeepingData.title}
                        onChange={(e) => setBookkeepingData({ ...bookkeepingData, title: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Additional Data Title</Form.Label>
                      <Form.Control
                        type="text"
                        value={bookkeepingData.additionalTitle}
                        onChange={(e) => setBookkeepingData({ ...bookkeepingData, additionalTitle: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex gap-2 mb-3">
                  <Button variant="outline-primary" onClick={addRow}><FaPlus className="me-1" /> Add Row</Button>
                  <Button variant="outline-primary" onClick={addColumn}><FaPlus className="me-1" /> Add Column</Button>
                </div>
              </div>
            )}

            <div className="table-responsive">
              <Table striped bordered hover className="mb-4">
                <thead className="table-light">
                  <tr>
                    {bookkeepingData.headers?.map((header, index) => (
                      <th key={index}>
                        {editMode ? (
                          <>
                            <Form.Control
                              type="text"
                              value={header}
                              onChange={(e) => handleHeaderChange(index, e.target.value)}
                              className="mb-1"
                            />
                            {index !== 0 && (
                              <Button variant="outline-danger" size="sm" onClick={() => removeColumn(index)} className="mt-1">
                                <FaMinus size={10} />
                              </Button>
                            )}
                          </>
                        ) : (
                          header
                        )}
                      </th>
                    ))}
                    {editMode && <th>Include</th>}
                    {editMode && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {bookkeepingData.rows?.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {bookkeepingData.headers.map((header, colIndex) => (
                        <td key={colIndex}>
                          {editMode ? (
                            <Form.Control
                              type="text"
                              value={row[header] || ''}
                              onChange={(e) => handleCellChange(rowIndex, header, e.target.value)}
                            />
                          ) : (
                            row[header]
                          )}
                        </td>
                      ))}
                      {editMode && (
                        <>
                          <td>
                            <Form.Check 
                              type="checkbox" 
                              checked={row.include} 
                              onChange={(e) => handleIncludeChange(rowIndex, e.target.checked)} 
                            />
                          </td>
                          <td>
                            <Button variant="outline-danger" onClick={() => removeRow(rowIndex)}><FaTrashAlt /></Button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {bookkeepingData.chartData && (
              <div style={{ height: '400px' }}>
                <Bar data={bookkeepingData.chartData} options={chartOptions} />
              </div>
            )}
          </Card.Body>
        </Card>

        {excludedRows.length > 0 && (
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">{bookkeepingData.additionalTitle}</h5>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead className="table-light">
                    <tr>
                      {bookkeepingData.headers.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {excludedRows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {bookkeepingData.headers.map((header, colIndex) => (
                          <td key={colIndex}>{row[header]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EmployeeBookkeeping;
